import React from 'react';
import DashboardLayout from '../../components/dashboard/DashboardLayout';
import { Info, FileText, Clock, Mail, Users, ClipboardCheck, NotebookPen, Square } from 'lucide-react';

const ChecklistSection = ({ icon: Icon, title, items }) => (
    <div className="bg-blue-50 p-6 rounded-lg mb-6">
      <div className="flex items-start gap-4">
        <Icon className="w-5 h-5 text-blue-600 flex-shrink-0" />
        <div className="w-full">
          <h3 className="font-semibold mb-4">{title}</h3>
          <ul className="space-y-4">
            {items.map((item, index) => (
              <li key={index} className="flex items-start gap-3">
                <Square className="w-5 h-5 text-gray-400 flex-shrink-0 mt-1" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );


const AfterTheInterview = () => {
    return (
        <DashboardLayout>
    <div className="max-w-4xl mx-auto px-6 py-12 text-gray-800">
      {/* Header */}
      <h1 className="text-4xl font-bold mb-8">Post-Interview Follow-Up Checklist</h1>

      {/* Info Box */}
      <div className="bg-gray-100 p-6 rounded-lg mb-12">
        <div className="flex items-start gap-3 mb-4">
          <Info className="w-5 h-5 mt-1" />
          <div>
            <h2 className="font-medium mb-2">Essential Overview</h2>
            <p className="text-gray-700">
              The hours and days following your interview are crucial for maintaining momentum and showing 
              professional courtesy. Use this checklist to ensure you make a strong post-interview impression 
              and maximize your chances of success.
            </p>
          </div>
        </div>
      </div>

      {/* Immediate Actions */}
      <ChecklistSection
        icon={Clock}
        title="Immediate Post-Interview Actions"
        items={[
          "Document the full name and title of everyone you met with",
          "Note down the exact job title discussed",
          "Record any specific projects or challenges mentioned",
          "Write down key company initiatives discussed",
          "Note any follow-up items you promised during the interview"
        ]}
      />

      {/* Thank You Email Guidelines */}
      <ChecklistSection
        icon={Mail}
        title="Thank You Email Essentials"
        items={[
          "Send within 24 hours of the interview",
          "Keep the message concise (maximum four sentences)",
          "Personalize for each interviewer if you met with multiple people",
          "Double-check all names and titles for accuracy",
          "Use a clear subject line (e.g., 'Thank You - [Position] Interview')"
        ]}
      />

      {/* Email Content Structure */}
      <div className="bg-blue-50 p-6 rounded-lg mb-6">
        <div className="flex items-start gap-4">
          <FileText className="w-5 h-5 text-blue-600" />
          <div>
            <h3 className="font-semibold mb-4">Thank You Email Template</h3>
            <div className="bg-white p-4 rounded-lg mb-4">
              <p className="font-medium text-green-600 mb-2">✓ EXAMPLE EMAIL:</p>
              <p className="mb-2">Subject: Thank You - Director of Marketing Interview</p>
              <p className="mb-4">Joe,</p>
              <p className="mb-2">I appreciate you taking time to speak with me about the Director of Marketing position. I enjoyed our conversation and learning about the role and your company. I look forward to meeting more of your team members.</p>
              <p className="mb-2">Best regards,</p>
              <p>Mike Sudermann</p>
            </div>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <Square className="w-5 h-5 text-gray-400 flex-shrink-0 mt-1" />
                <span>Include specific role title discussed</span>
              </li>
              <li className="flex items-start gap-3">
                <Square className="w-5 h-5 text-gray-400 flex-shrink-0 mt-1" />
                <span>Reference a specific topic from your conversation</span>
              </li>
              <li className="flex items-start gap-3">
                <Square className="w-5 h-5 text-gray-400 flex-shrink-0 mt-1" />
                <span>Express enthusiasm about next steps</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Additional Follow-Up */}
      <ChecklistSection
        icon={NotebookPen}
        title="Additional Follow-Up Items"
        items={[
          "Send any requested materials or references promptly",
          "Connect with interviewers on LinkedIn (if appropriate)",
          "Document any salary or benefit details discussed",
          "Note expected timeline for next steps",
          "Set a reminder to follow up if you don't hear back within the discussed timeframe"
        ]}
      />

      {/* Professional Etiquette */}
      <ChecklistSection
        icon={Users}
        title="Professional Etiquette Reminders"
        items={[
          "Maintain enthusiasm but keep communication professional",
          "Avoid sending multiple follow-up emails in quick succession",
          "Consider sending a separate note to HR or the recruiter if they were involved",
          "Keep track of all communication for future reference",
          "Update your job search tracking system with interview details and next steps"
        ]}
      />

      {/* Quality Check */}
      <ChecklistSection
        icon={ClipboardCheck}
        title="Final Quality Check"
        items={[
          "Proofread all follow-up communication carefully",
          "Verify email addresses before sending",
          "Ensure all promised follow-up materials are attached",
          "Check that your signature block is professional and complete",
          "Save copies of all sent communication"
        ]}
      />
    </div>
    </DashboardLayout>
    );
};

export default AfterTheInterview; 